import { AlertCircle, Check, Loader2, ShieldOff, Wrench } from 'lucide-react'

import { cn } from '../cn'

export type ToolCallChipStatus = 'pending' | 'ok' | 'error' | 'denied' | 'timeout' | undefined

export interface ToolCallChipProps {
  /** Tool slug, rendered in mono font. */
  slug: string
  /**
   * Lifecycle status. Undefined / "pending" → in-flight (spinner). "ok" →
   * emerald edge confirmation. "error" / "timeout" → destructive. "denied"
   * → orange (permission failure).
   */
  status?: ToolCallChipStatus | string
  className?: string
}

/**
 * In-flight tool-call indicator pill rendered inline next to a tool's
 * citations. Three visual states (per `apps/agent/plans/plan-action-cards-polish.md`):
 *
 *  - **pending** — pulsing Loader2 + "Calling …" in muted-neutral palette
 *  - **ok**      — Check + "Used …" with emerald edge, no body fill
 *  - **error / denied / timeout** — meaningful accent (destructive / orange)
 *
 * Lifted from `apps/agent/client/src/pages/chat.tsx::ToolCallChip`.
 */
function ToolCallChip({ slug, status, className }: ToolCallChipProps) {
  const isInFlight = !status || status === 'pending'
  const isError = status === 'error' || status === 'timeout'
  const isDenied = status === 'denied'

  const tone = isInFlight
    ? 'border-border bg-muted/40 text-muted-foreground'
    : isError
      ? 'border-destructive/40 bg-destructive/10 text-destructive'
      : isDenied
        ? 'border-orange-300/60 bg-orange-50 text-orange-900 dark:border-orange-500/40 dark:bg-orange-500/10 dark:text-orange-200'
        : 'border-emerald-500/30 bg-card text-foreground'

  const Icon = isInFlight ? Loader2 : isError ? AlertCircle : isDenied ? ShieldOff : Check
  const label = isInFlight
    ? `Calling ${slug}…`
    : isError
      ? `${slug} ${status === 'timeout' ? 'timed out' : 'failed'}`
      : isDenied
        ? `${slug} denied`
        : `Used ${slug}`

  return (
    <div
      className={cn(
        'typo-meta inline-flex max-w-full items-center gap-1.5 rounded-md border px-2 py-1 font-medium',
        tone,
        className,
      )}>
      <Icon className={cn('h-3 w-3 shrink-0', isInFlight && 'animate-spin')} aria-hidden />
      <Wrench className="h-3 w-3 shrink-0 opacity-50" aria-hidden />
      <span className="typo-caption truncate font-mono">{label}</span>
    </div>
  )
}

export { ToolCallChip }
